import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useLocation } from '@tanstack/react-router';
import { X } from 'lucide-react';
import { Button } from '../ui/button';
import { Sidebar } from '../chat/Sidebar';
import { AppShellContext } from './useAppShell';
import { cn } from '../../lib/utils';
import { studioBOn } from '../../lib/studioB/flag';

const TABLET_QUERY = '(min-width: 768px)';
const WIDE_QUERY = '(min-width: 1280px)';
/** Studio B keeps the full rail on an iPad in landscape. */
const WIDE_QUERY_B = '(min-width: 1024px)';
const RAIL_KEY = 'ivy.rail.collapsed';

function useMedia(query: string) {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = (e: MediaQueryListEvent) => setMatches(e.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);
  return matches;
}

function readCollapsed() {
  try {
    return localStorage.getItem(RAIL_KEY) === '1';
  } catch {
    return false;
  }
}

function writeCollapsed(collapsed: boolean) {
  try {
    localStorage.setItem(RAIL_KEY, collapsed ? '1' : '0');
  } catch {
    // the choice lasts for this page only
  }
}

export function AppShell({ children }: { children: ReactNode }) {
  const b = studioBOn();
  const isPhone = !useMedia(TABLET_QUERY);
  const isWide = useMedia(b ? WIDE_QUERY_B : WIDE_QUERY);
  const [collapsedPref, setCollapsedPref] = useState(readCollapsed);
  const [narrowOpen, setNarrowOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const pathname = useLocation({ select: (l) => l.pathname });

  useEffect(() => setDrawerOpen(false), [pathname, isPhone]);
  useEffect(() => setNarrowOpen(false), [isWide]);

  const collapsed = isWide ? collapsedPref : !narrowOpen;
  const toggle = useCallback(() => {
    if (!isWide) return setNarrowOpen((open) => !open);
    setCollapsedPref((was) => {
      writeCollapsed(!was);
      return !was;
    });
  }, [isWide]);

  return (
    <AppShellContext.Provider value={{ isPhone, railOpen: !isPhone && !collapsed, logoInBar: b ? isPhone : isPhone || collapsed, openNav: () => setDrawerOpen(true) }}>
      <div data-shell={b ? 'b' : undefined} className="flex h-screen w-full overflow-hidden bg-background">
        {!isPhone && (
          <aside
            data-testid="app-rail"
            data-collapsed={collapsed}
            className={cn(
              'shrink-0 overflow-x-hidden transition-[width]',
              b ? 'duration-b-move ease-b motion-reduce:transition-none' : 'duration-200',
              b
                ? 'm-3 h-[calc(100%-24px)] rounded-2xl bg-b-panel pb-[env(safe-area-inset-bottom)] shadow-[0_0_0_1px_hsl(var(--b-line)),0_1px_2px_rgb(23_18_33/0.05),0_18px_40px_-24px_rgb(23_18_33/0.28)] dark:shadow-[0_0_0_1px_hsl(var(--b-line)),0_18px_40px_-20px_rgb(0_0_0/0.7)]'
                : 'border-r border-border bg-card',
              collapsed ? 'w-16' : 'w-[232px]',
            )}
          >
            <Sidebar collapsed={collapsed} onToggle={toggle} />
          </aside>
        )}
        <div data-shell-stage className="relative h-full min-w-0 flex-1">{children}</div>
      </div>

      {isPhone && drawerOpen && (
        <div className="fixed inset-0 z-[70] flex" role="dialog" aria-modal="true" aria-label="Menu">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm animate-in fade-in-0 duration-150" onClick={() => setDrawerOpen(false)} />
          <aside className="relative z-10 w-[280px] max-w-[85vw] overflow-x-hidden border-r border-border bg-card shadow-[var(--shadow-panel)] animate-in slide-in-from-left-4 duration-200">
            <Button variant="ghost" size="sm" onClick={() => setDrawerOpen(false)} className="absolute right-2 top-3 z-10 h-8 w-8 p-0" title="Close menu">
              <X className="h-4 w-4" />
            </Button>
            <Sidebar showLogo={false} onChatSelect={() => setDrawerOpen(false)} />
          </aside>
        </div>
      )}
    </AppShellContext.Provider>
  );
}
